import Box from 'components/shared/Box'
import { defineMessage, FormattedMessage, useIntl } from 'react-intl'
import { useResolutions } from 'store/resolutions'
import Link from 'components/shared/Link'
import Text from 'components/shared/Text'
import { useApp } from 'store/steps'
import { useEffect, useState } from 'react'
import { SkipStepModal } from './SkipStepModal'
import { useCart } from 'store/cart-slice/hooks'
import { useAppSelector } from 'store/hooks'
import BackToEditConfirmationModal from 'routes/selfcare/views/SelfcareSummary/BackToEditConfirmationModal'
import { HOME_LINK, VOUCHERS_LANDING_PAGE } from 'constants/links'
import steps from 'index'
import { useAccommodation } from 'store/accommodation-slice/hooks'
import { pushDataLayer } from 'helpers/analytics'
import { useSelfcareCart } from 'store/selfcare-cart-slice/hooks'

interface StepHeadingProps {
    title: string
    withGoingBackDisabled?: boolean
}

const StepHeading = ({ title, withGoingBackDisabled }: StepHeadingProps) => {
    const { formatMessage } = useIntl()
    const { isTablet } = useResolutions()
    const {
        stepIndex,
        stepId,
        navigateToStep,
        goBackToStep,
        isCreatorAccommodation,
        isUnlimitedMode,
    } = useApp()
    const { isStepEmpty, isCartEmpty, isCartFetching } = useCart()
    const { isOrderModified } = useSelfcareCart()
    const { isAccommodationProceeding } = useAccommodation()

    const { appMode } = useAppSelector((state) => state.general)

    const [isModalOpen, setIsModalOpen] = useState(false)
    const [isStarterModalOpen, setIsStarterModalOpen] = useState(false)
    const [isSkipDisabled, setIsSkipDisabled] = useState(true)

    const isVouchersMode = appMode === 'vouchers'
    const goBackInitialLink = isVouchersMode ? VOUCHERS_LANDING_PAGE : HOME_LINK
    const nextStep = steps[stepIndex + 1]?.navigationIndex
        ? steps[stepIndex + 1]
        : steps[stepIndex + 2]

    const goToNextStep = () => {
        // < analytics
        const isTicketsStep = stepId === 'tickets'
        const isUpsellsStep = stepId === 'upsell'
        const isAccommodationStep =
            stepId === 'accommodation' || stepId === 'accommodation_details'
        const eventCategory = !isCreatorAccommodation
            ? 'tickets'
            : 'accommodation'

        if (isTicketsStep) {
            pushDataLayer({
                event: 'funnelStep',
                event_category: eventCategory,
                event_action: 'tickets_params_selected',
                event_label: '',
            })
        }
        if (isUpsellsStep) {
            pushDataLayer({
                event: 'funnelStep',
                event_category: eventCategory,
                event_action: 'extra_services_selected',
                event_label: '',
            })
        }
        if (isAccommodationStep) {
            pushDataLayer({
                event: 'funnelStep',
                event_category: eventCategory,
                event_action: 'accommodation_params_selected',
                event_label: '',
            })
        }
        // analytics >

        navigateToStep({ stepId: nextStep.id })
    }

    let goBackLabel = defineMessage({
        id: 'global.btn.back',
        defaultMessage: 'Wróć',
    })

    switch (stepId) {
        case 'starter':
        case 'renew_summary':
            goBackLabel = defineMessage({
                id: 'global.btn.back_to_homepage',
                defaultMessage: 'Wróć do strony głównej',
            })
            break
        case 'edit_order':
            goBackLabel = defineMessage({
                id: 'global.btn.back_to_order_details',
                defaultMessage: 'Wróć do szczegółów rezerwacji',
            })
            break
        case 'summary':
            goBackLabel = defineMessage({
                id: 'global.btn.back_to_orders',
                defaultMessage: 'Wróć do swoich rezerwacji',
            })
            break
        case 'card_configuration':
            goBackLabel = defineMessage({
                id: 'annual.leave_configurator',
                defaultMessage: 'Opuść konfigurator',
            })
    }

    useEffect(() => {
        const shouldSkipBeDisabled =
            stepId === 'order' ||
            appMode === 'selfcare' ||
            appMode === 'unlimited-card' ||
            appMode === 'unlimited-renew' ||
            appMode === 'vouchers' ||
            ((stepId === 'accommodation' ||
                stepId === 'accommodation_details') &&
                (isCartEmpty || isCartFetching) &&
                appMode === 'creator') ||
            (stepId === 'upsell' &&
                isCreatorAccommodation &&
                (isCartEmpty ||
                    isCartFetching ||
                    (isStepEmpty('accommodation') && isStepEmpty('tickets'))))

        setIsSkipDisabled(shouldSkipBeDisabled)
    }, [isCartEmpty, isCartFetching])

    const handleSkipStep = () =>
        !!stepId &&
        isStepEmpty(stepId) &&
        ((stepId !== 'accommodation_details' && stepId !== 'accommodation') ||
            !isAccommodationProceeding)
            ? goToNextStep()
            : setIsModalOpen(true)

    const handleGoBack = () => {
        if (stepIndex === 0) return
        goBackToStep(steps[stepIndex - 1].id)
    }

    const handleStarterModal = () => {
        isOrderModified ? setIsStarterModalOpen(true) : goBackToStep('starter')
    }

    const handleGoHome = () => {
        location.replace(HOME_LINK)
    }

    let goBackFunction

    switch (stepId) {
        case 'edit_order':
            goBackFunction = handleStarterModal
            break
        case 'starter':
        case 'renew_summary':
            goBackFunction = handleGoHome
            break
        default:
            goBackFunction = handleGoBack
    }

    return (
        <>
            {(isTablet ||
                isUnlimitedMode ||
                isVouchersMode ||
                (appMode === 'selfcare' &&
                    (stepId === 'starter' || stepId === 'edit_order'))) &&
                (!withGoingBackDisabled || !isSkipDisabled) && (
                    <Box
                        direction="row"
                        justify="space-between"
                        align="center"
                        mb={16}
                    >
                        {!withGoingBackDisabled && (
                            <Link
                                variant={
                                    isUnlimitedMode || isVouchersMode
                                        ? 'secondary'
                                        : 'primary'
                                }
                                path={
                                    stepIndex === 0
                                        ? goBackInitialLink
                                        : undefined
                                }
                                onClick={goBackFunction}
                                iconPosition="left"
                                iconRotation={180}
                                data-testid="step-button-go-back"
                            >
                                {formatMessage(goBackLabel)}
                            </Link>
                        )}
                        {!isSkipDisabled && (
                            <Link
                                variant="secondary"
                                onClick={handleSkipStep}
                                data-testid="step-button-skip"
                            >
                                <FormattedMessage
                                    id="global.btn.skip_step"
                                    defaultMessage="Pomiń ten krok"
                                />
                            </Link>
                        )}
                    </Box>
                )}
            <Box direction="row" justify="space-between" align="center">
                <Text variant="heading1" as={'h1'}>
                    {title}
                </Text>
                {!isTablet && !isSkipDisabled && (
                    <Link
                        variant="secondary"
                        onClick={handleSkipStep}
                        data-testid="step-button-skip"
                        focusable={stepId !== 'tickets'}
                    >
                        <FormattedMessage
                            id="global.btn.skip_step"
                            defaultMessage="Pomiń ten krok"
                        />
                    </Link>
                )}
            </Box>

            <SkipStepModal
                isOpen={
                    !!stepId &&
                    (!isStepEmpty(stepId) || isAccommodationProceeding) &&
                    isModalOpen
                }
                onReset={() => setIsModalOpen(false)}
                nextStep={goToNextStep}
            />
            {appMode === 'selfcare' && (
                <BackToEditConfirmationModal
                    isModalInfoOpen={isStarterModalOpen}
                    setIsModalInfoOpen={() => setIsStarterModalOpen(false)}
                />
            )}
        </>
    )
}
export default StepHeading
